import React, { MouseEventHandler } from 'react';
import { ButtonProps, VXButton } from './VXButton';
import Icon from 'src/components/common/maintain-table/bootstrap/Icon';
import { ElementWithTooltip } from 'src/components/common/vx/element-with-tooltip/ElementWithTooltip';
import { Placement } from 'react-bootstrap/types';

export type ActionButtonDO = {
  label?: string | React.JSX.Element;
  action?:
    | 'default'
    | 'warning'
    | 'failed'
    | 'save'
    | 'refresh'
    | 'new'
    | 'update'
    | 'edit'
    | 'cancel'
    | 'delete'
    | 'exit'
    | 'nist';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  extraClass?: string;
  icon?: string;
  cy?: string;
  tc?: string;
  disabled?: boolean;
  type?: 'primary' | 'secondary' | 'vx-border' | ButtonProps['variant'];
  imageSrc?: string;
  autoWidth?: boolean;
  tooltip?: React.JSX.Element;
  iconPosition?: 'left' | 'right';
  asTab?: boolean;
  tooltipPlacement?: Placement;
  size?: ButtonProps['size'];
};

function mapVariant(
  action: ActionButtonDO['action'],
  type?: ActionButtonDO['type'],
  asTab?: boolean,
): ButtonProps['variant'] {
  if (asTab) return 'ghost';

  // Soft dismiss: Cancel uses ghost destructive when requested via type.
  if (action === 'cancel' && (type === 'destructive-ghost' || type === 'ghost')) {
    return type;
  }

  // Hard destructive intents (delete / failed / warning).
  const destructiveActions: ActionButtonDO['action'][] = ['delete', 'failed', 'warning'];

  if (destructiveActions.includes(action)) {
    return type === 'vx-border' || type === 'destructive-ghost'
      ? 'destructive-ghost'
      : 'destructive';
  }

  const vxVariants: ButtonProps['variant'][] = [
    'primary',
    'secondary',
    'outline',
    'ghost',
    'destructive',
    'destructive-ghost',
  ];

  if (type && vxVariants.includes(type as ButtonProps['variant'])) {
    return type as ButtonProps['variant'];
  }

  if (type === 'vx-border') return 'outline';
  if (type === 'secondary') return 'secondary';

  return 'primary';
}

export default function ActionButton({
  onClick,
  label,
  action,
  icon,
  cy,
  tc,
  disabled,
  type = 'primary',
  imageSrc,
  tooltip,
  iconPosition = 'left',
  asTab,
  tooltipPlacement = 'auto',
  extraClass = '',
  size = 'sm',
}: ActionButtonDO) {
  const variant = mapVariant(action, type, asTab);
  const resolvedIcon =
    icon ?? (action === 'refresh' ? 'fa-solid fa-arrows-rotate' : undefined);

  const leftIcon = imageSrc ? (
    <img
      src={imageSrc}
      alt="icon"
    />
  ) : resolvedIcon && iconPosition === 'left' ? (
    <Icon icon={resolvedIcon} />
  ) : undefined;

  const rightIcon =
    resolvedIcon && iconPosition === 'right' ? <Icon icon={resolvedIcon} /> : undefined;

  const content = (
    <VXButton
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      size={size ? size : !label ? 'sm' : 'sm'}
      data-cy={cy}
      data-tc={tc ?? `action-${typeof label === 'string' ? label : action}`}
      className={extraClass}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
    >
      {label}
    </VXButton>
  );

  const hasTooltip =
    !!tooltip && React.Children.toArray(tooltip.props.children).some((child) => child !== '');

  return hasTooltip ? (
    <ElementWithTooltip
      placement={tooltipPlacement}
      element={<div>{content}</div>}
      tooltip={tooltip}
    />
  ) : (
    content
  );
}
